/**
 * Hook: useCurrentLocale
 * 
 * Provides current locale without requiring useTranslation() import.
 * Use this instead of importing useTranslation just to get i18n.language.
 * 
 * This hook is part of the localization system's controlled access pattern:
 * - useLocalizations() for l*() translation functions
 * - useCurrentLocale() for locale string access
 * - L* components for visible text in JSX
 * 
 * @example
 * // Instead of:
 * import { useTranslation } from 'react-i18next';
 * const { i18n } = useTranslation();
 * const locale = i18n.language;
 * 
 * // Use:
 * import { useCurrentLocale } from '@/hooks/useCurrentLocale';
 * const locale = useCurrentLocale();
 */

import { useLocale } from '@/contexts/LocaleContext';
import { DEFAULT_REGIONAL_LOCALE } from '@/constants/localization';

/**
 * Get current locale without importing useTranslation
 * @returns Current locale string (e.g., 'en-US', 'es-ES')
 */
export function useCurrentLocale(): string {
  const { settings } = useLocale();
  return settings?.locale || DEFAULT_REGIONAL_LOCALE;
}

/**
 * Get current base locale (2-letter language code)
 * 
 * @deprecated Use usePrimaryLocale().resolveToPrimary() for content localization.
 * This hook uses naive locale.split('-')[0] which incorrectly handles:
 * - zh-TW (should stay zh-TW, not become 'zh')
 * - zh-HK (should map to zh-TW, not 'zh')
 * 
 * @returns Base locale (e.g., 'en', 'es', 'pt')
 */
export function useBaseLocale(): string {
  const locale = useCurrentLocale();
  // eslint-disable-next-line no-console
  if (process.env.NODE_ENV === 'development') {
    console.warn('[useBaseLocale] Deprecated: Use usePrimaryLocale().resolveToPrimary() for content localization.');
  }
  return locale.split('-')[0];
}

/**
 * Get current country code from locale
 * @returns Country code (e.g., 'US', 'ES', 'BR') or undefined
 */
export function useCountryCode(): string | undefined {
  const locale = useCurrentLocale();
  const parts = locale.split('-');
  return parts.length > 1 ? parts[1] : undefined;
}
